import { useEffect, useRef } from "react";
import { Loc, t } from "@/locales";
import { QRCode } from "@/shared/presentation/components/QRCode";

const QR_SIZE_PX = 208;

type Props = {
  // Link the phone should open: this page, with the current configuration and
  // the AR auto-start flag.
  url: string;
  onClose: () => void;
};

/**
 * Desktop stand-in for AR: phones can enter AR directly, desktops cannot, so the
 * configuration travels to the phone as a QR code instead of a dead end.
 *
 * Deliberately rendered inside the widget (no portal to `document.body`): the
 * widget lives in a Shadow DOM whose styles do not apply outside it.
 */
export function ARQRModal({ url, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="ar-qr-modal"
      role="dialog"
      aria-modal="true"
      aria-label={t(Loc.arOpen)}
    >
      <div
        className="ar-qr-modal__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="ar-qr-modal__panel">
        <button
          ref={closeRef}
          type="button"
          className="ar-qr-modal__close"
          onClick={onClose}
          aria-label={t(Loc.close)}
        >
          ×
        </button>

        <div className="ar-qr-modal__title">{t(Loc.arOpen)}</div>
        <p className="ar-qr-modal__text">{t(Loc.arModalText)}</p>

        <div className="ar-qr-modal__code">
          <QRCode value={url} size={QR_SIZE_PX} title={t(Loc.arQrCodeTitle)} />
        </div>

        <div className="ar-qr-modal__hint">{t(Loc.arModalHint)}</div>
      </div>
    </div>
  );
}
